/*
 * Copyright 2025 Mia srl
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState, useEffect } from 'react';
import analyticsService from '../services/analyticsService';
import { useAnalytics } from '../hooks/useAnalytics';
import './Privacy.css';

const Privacy = () => {
  const { trackAction } = useAnalytics();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    // Check if analytics is enabled
    const enabled = analyticsService.isAnalyticsEnabled();
    setAnalyticsEnabled(enabled);

    // Update session duration every second
    const interval = setInterval(() => {
      const duration = analyticsService.getSessionDuration();
      setSessionDuration(duration);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleToggleAnalytics = () => {
    if (analyticsEnabled) {
      // Track before disabling
      trackAction('settings', 'analytics_toggle', { enabled: false });
      analyticsService.disable();
      setAnalyticsEnabled(false);
      showToast('Analytics tracking disabled', 'success');
    } else {
      analyticsService.enable();
      trackAction('settings', 'analytics_toggle', { enabled: true });
      setAnalyticsEnabled(true);
      showToast('Analytics tracking enabled', 'success');
    }
  };

  const handleClearData = () => {
    analyticsService.clearData();
    setShowClearConfirm(false);
    showToast('Analytics data cleared', 'success');
  };

  const showToast = (message, type) => {
    window.dispatchEvent(new CustomEvent('show-toast', {
      detail: { message, type }
    }));
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <div className="privacy-container">
      <h1>Privacy & Analytics</h1>
      <p className="privacy-description">
        Control how Mia Flow collects and uses your data to improve your experience.
      </p>

      <div className="privacy-section">
        <div className="privacy-card">
          <div className="privacy-card-header">
            <h2>Analytics Tracking</h2>
            <label className="switch">
              <input
                type="checkbox"
                checked={analyticsEnabled}
                onChange={handleToggleAnalytics}
              />
              <span className="slider"></span>
            </label>
          </div>
          
          <p className="privacy-card-description">
            {analyticsEnabled 
              ? 'We collect anonymous usage data to improve the application.' 
              : 'Analytics tracking is disabled. No usage data is being collected.'}
          </p>

          {analyticsEnabled && (
            <div className="privacy-info">
              <div className="info-item">
                <span className="info-label">Current Session:</span>
                <span className="info-value">{formatDuration(sessionDuration)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="privacy-card">
          <div className="privacy-card-header">
            <h2>What We Collect</h2>
          </div>
          
          <ul className="privacy-list">
            <li>
              <strong>User Actions:</strong> Button clicks, page views, feature usage
            </li>
            <li>
              <strong>Technical Data:</strong> Browser type, screen resolution, performance metrics
            </li>
            <li>
              <strong>Session Data:</strong> Session duration, active time, navigation patterns
            </li>
            <li>
              <strong>Errors:</strong> Application errors and debugging information
            </li>
          </ul>

          <p className="privacy-note">
            ℹ️ We do not collect personally identifiable information (PII) or conversation content.
          </p>
        </div>

        <div className="privacy-card">
          <div className="privacy-card-header">
            <h2>Data Retention</h2>
          </div>
          
          <p className="privacy-card-description">
            Your analytics data is automatically deleted after 90 days. You can also manually clear 
            your data at any time using the button below.
          </p>

          {!showClearConfirm ? (
            <button 
              className="clear-data-button"
              onClick={() => setShowClearConfirm(true)}
            >
              Clear My Data
            </button>
          ) : (
            <div className="clear-confirm">
              <p>Are you sure you want to clear all your analytics data?</p>
              <div className="clear-confirm-buttons">
                <button 
                  className="confirm-button danger"
                  onClick={handleClearData}
                >
                  Yes, Clear Data
                </button>
                <button 
                  className="confirm-button"
                  onClick={() => setShowClearConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="privacy-card">
          <div className="privacy-card-header">
            <h2>Your Rights</h2>
          </div>
          
          <p className="privacy-card-description">
            You have the right to:
          </p>

          <ul className="privacy-list">
            <li>Opt out of analytics tracking at any time</li>
            <li>Request deletion of your analytics data</li>
            <li>Access your collected data</li>
            <li>Export your analytics information</li>
          </ul>

          <p className="privacy-note">
            ℹ️ For data export or additional privacy requests, please contact support.
          </p>
        </div>

        <div className="privacy-card">
          <div className="privacy-card-header">
            <h2>GDPR Compliance</h2>
          </div>
          
          <p className="privacy-card-description">
            Mia Flow is fully compliant with GDPR regulations. We:
          </p>

          <ul className="privacy-list">
            <li>Process data lawfully and transparently</li>
            <li>Collect only necessary data for improving the service</li>
            <li>Store data securely with encryption</li>
            <li>Automatically delete data after the retention period</li>
            <li>Respect your right to erasure and data portability</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
